/**
 * DTO Mappers pentru alinierea Frontend ↔ Backend
 * 
 * Rezolvă problema de nealinier între:
 * - camelCase (FE) ↔ snake_case (BE)
 * - Date objects ↔ ISO strings
 * - Enum values diferite
 * 
 * NOTĂ: Acest mapper este adaptat la tipurile reale din types/visit.ts
 */

import type { 
  VisitWithContext, 
  VisitFormState, 
  VisitStatus, 
  VisitDocumentStatus,
  VitalsData,
  AnamnesisData,
  ExamData,
  PlanData,
  ProblemData,
  TreatmentData
} from '@app-types/visit';

// Types pentru API responses (snake_case)
export interface ApiVisit {
  id: string;
  clinic_id: string;
  patient_id: string;
  owner_id: string;
  veterinarian_id: string;
  appointment_id?: string | null;
  visit_date: string;
  visit_type: string;
  status: string;
  document_status?: string;
  chief_complaint?: string | null;
  history?: string | null;
  physical_exam?: string | null;
  diagnosis?: string | null;
  treatment_plan?: string | null;
  medications?: string | null;
  recommendations?: string | null;
  follow_up_notes?: string | null;
  visit_duration?: number | null;
  cost?: number | null;
  started_at?: string | null;
  operational_status?: string | null;
  signed_at?: string | null;
  signed_by?: string | null;
  locked_at?: string | null;
  created_at: string;
  updated_at: string;
  
  // Joined data (optional)
  animal?: {
    id: string;
    name: string;
    species: string;
    breed?: string;
    date_of_birth?: string;
    weight?: number;
    microchip?: string;
    allergies?: string[];
  };
  
  owner?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
  };
  
  services?: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  
  // Form data (JSONB fields)
  vitals?: any;
  anamnesis?: any;
  exam?: any;
  problems?: any[];
  plan?: any;
  treatments?: any[];
  vaccination?: any;
}

// API Form Data (snake_case pentru request)
export interface ApiVisitFormData {
  vitals?: Partial<{
    temperature?: number;
    heart_rate?: number;
    respiratory_rate?: number;
    weight?: number;
    bcs?: number;
    crt?: number;
    pain_score?: number;
    mucosa?: string;
    recorded_at?: string;
  }>;
  
  anamnesis?: Partial<{
    nutrition?: string;
    deworming?: string;
    deworming_last?: string;
    current_meds?: string;
    allergies?: string;
    environment?: string;
    reproduction?: string;
  }>;
  
  exam?: Partial<{
    systems?: Record<string, string>;
    system_notes?: Record<string, string>;
    notes?: string;
    all_normal?: boolean;
  }>;
  
  problems?: Array<Partial<{
    id?: string;
    description: string;
    severity?: string;
    type?: string;
    icd_code?: string;
    resolved?: boolean;
  }>>;
  
  plan?: Partial<{
    investigations?: string;
    treatments?: string;
    home_care?: string;
    recall_date?: string;
    recall_reason?: string;
  }>;
  
  treatments?: Array<Partial<{
    id?: string;
    drug: string;
    dose: string;
    route: string;
    time?: string;
    qty?: number;
    uom?: string;
    lot?: string;
    exp?: string;
  }>>;
  
  vaccination?: Partial<{
    product?: string;
    lot: string;
    exp: string;
    scheme?: string;
    next_due?: string;
  }>;
  
  notes?: string;
  recommendations?: string;
}

/**
 * Convertește API response (snake_case) la VisitWithContext (camelCase)
 * Adaptat la tipurile reale din types/visit.ts
 */
export function fromApiVisit(apiVisit: ApiVisit): VisitWithContext {
  const normalizeStatus = (status: string): VisitStatus => {
    const statusMap: Record<string, VisitStatus> = {
      planned: 'planned',
      draft: 'draft',
      in_progress: 'in_progress',
      'in-progress': 'in_progress',
      signed: 'signed',
      locked: 'locked',
      completed: 'completed',
      cancelled: 'cancelled',
      closed: 'completed',
    };
    return statusMap[status.toLowerCase()] || 'draft';
  };

  const normalizeDocumentStatus = (status?: string): VisitDocumentStatus => {
    if (!status) return 'draft';
    const docStatusMap: Record<string, VisitDocumentStatus> = {
      'draft': 'draft',
      'signed': 'signed',
      'locked': 'locked'
    };
    return docStatusMap[status.toLowerCase()] || 'draft';
  };

  // Parse JSONB fields safely
  let vitals: VitalsData | undefined;
  if (apiVisit.vitals && typeof apiVisit.vitals === 'object') {
    vitals = {
      temperature: apiVisit.vitals.temperature || apiVisit.vitals.temperature_c,
      heartRate: apiVisit.vitals.heart_rate || apiVisit.vitals.heartRate,
      respiratoryRate: apiVisit.vitals.respiratory_rate || apiVisit.vitals.respiratoryRate,
      crt: apiVisit.vitals.crt || apiVisit.vitals.crt_seconds,
      painScore: apiVisit.vitals.pain_score || apiVisit.vitals.painScore,
      bcs: apiVisit.vitals.bcs,
      mucosa: apiVisit.vitals.mucosa || apiVisit.vitals.mucous_color,
      weight: apiVisit.vitals.weight || apiVisit.vitals.weight_kg,
      recordedAt: apiVisit.vitals.recorded_at
    };
  }

  let anamnesis: AnamnesisData | undefined;
  if (apiVisit.anamnesis && typeof apiVisit.anamnesis === 'object') {
    anamnesis = {
      nutrition: apiVisit.anamnesis.nutrition,
      deworming: apiVisit.anamnesis.deworming,
      dewormingLast: apiVisit.anamnesis.deworming_last,
      currentMeds: apiVisit.anamnesis.current_meds || apiVisit.anamnesis.currentMeds,
      allergies: apiVisit.anamnesis.allergies,
      environment: apiVisit.anamnesis.environment,
      reproduction: apiVisit.anamnesis.reproduction
    };
  }

  let exam: ExamData | undefined;
  if (apiVisit.exam && typeof apiVisit.exam === 'object') {
    exam = {
      systems: apiVisit.exam.systems || {},
      systemNotes: apiVisit.exam.system_notes || apiVisit.exam.systemNotes || {},
      notes: apiVisit.exam.notes,
      allNormal: apiVisit.exam.all_normal || apiVisit.exam.allNormal
    };
  }

  let problems: ProblemData[] | undefined;
  if (Array.isArray(apiVisit.problems)) {
    problems = apiVisit.problems.map((p: any) => ({
      id: p.id || `prob-${Date.now()}-${Math.random()}`,
      description: p.description || p.name || '',
      severity: p.severity || 'mild',
      type: p.type || 'presumptive',
      resolved: p.resolved || false,
      icdCode: p.icd_code || p.icdCode
    }));
  }

  let plan: PlanData | undefined;
  if (apiVisit.plan && typeof apiVisit.plan === 'object') {
    plan = {
      investigations: apiVisit.plan.investigations,
      treatments: apiVisit.plan.treatments,
      homeCare: apiVisit.plan.home_care || apiVisit.plan.homeCare,
      recallDate: apiVisit.plan.recall_date || apiVisit.plan.recallDate,
      recallReason: apiVisit.plan.recall_reason || apiVisit.plan.recallReason
    };
  }

  let treatments: TreatmentData[] | undefined;
  if (Array.isArray(apiVisit.treatments)) {
    treatments = apiVisit.treatments.map((t: any) => ({
      id: t.id || `treat-${Date.now()}-${Math.random()}`,
      drug: t.drug || t.medication_name || '',
      drugId: t.drug_id || t.medication_id,
      dose: t.dose || t.dosage || '',
      route: (t.route || 'oral') as any,
      time: t.time || new Date().toTimeString().substring(0, 5),
      qty: t.qty || 1,
      uom: t.uom || 'buc',
      lot: t.lot || t.lot_number,
      exp: t.exp || t.expiry_date,
      fromStock: t.from_stock || false
    }));
  }

  return {
    id: apiVisit.id,
    clinicId: apiVisit.clinic_id,
    petId: apiVisit.patient_id,
    ownerId: apiVisit.owner_id,
    doctorId: apiVisit.veterinarian_id,
    appointmentId: apiVisit.appointment_id || undefined,
    
    // Status workflow
    status: normalizeStatus(apiVisit.status),
    documentStatus: normalizeDocumentStatus(apiVisit.document_status),
    
    // Timestamps
    createdAt: apiVisit.created_at,
    updatedAt: apiVisit.updated_at,
    startedAt: (apiVisit as unknown as Record<string, unknown>).started_at
      ? String((apiVisit as unknown as Record<string, unknown>).started_at)
      : undefined,
    signedAt: apiVisit.signed_at ?? undefined,
    signedBy: apiVisit.signed_by ?? undefined,
    lockedAt: apiVisit.locked_at ?? undefined,
    
    // Context
    animal: apiVisit.animal ? {
      id: apiVisit.animal.id,
      name: apiVisit.animal.name,
      species: apiVisit.animal.species,
      breed: apiVisit.animal.breed,
      dateOfBirth: apiVisit.animal.date_of_birth,
      weight: apiVisit.animal.weight,
      microchip: apiVisit.animal.microchip,
      allergies: apiVisit.animal.allergies || []
    } : undefined,
    
    owner: apiVisit.owner ? {
      id: apiVisit.owner.id,
      firstName: apiVisit.owner.first_name,
      lastName: apiVisit.owner.last_name,
      email: apiVisit.owner.email,
      phone: apiVisit.owner.phone
    } : undefined,
    
    services: (apiVisit.services || []).map(s => ({
      id: s.id,
      name: s.name,
      type: (s.type || 'other') as 'consultation' | 'vaccination' | 'surgery' | 'diagnostic' | 'treatment' | 'other',
      duration: undefined,
      price: undefined
    })),
    
    // Clinical content
    visitType: apiVisit.visit_type,
    visitDate: apiVisit.visit_date,
    chiefComplaint: apiVisit.chief_complaint ?? undefined,
    
    // Form fields
    vitals,
    anamnesis,
    exam,
    problems,
    plan,
    treatments,
    vaccination: apiVisit.vaccination ? {
      lot: apiVisit.vaccination.lot || '',
      exp: apiVisit.vaccination.exp || '',
      product: apiVisit.vaccination.product,
      scheme: apiVisit.vaccination.scheme as any
    } : undefined,
    
    notes: apiVisit.history ?? undefined,
    recommendations: apiVisit.recommendations ?? undefined,
    
    // Computed
    duration: apiVisit.visit_duration || undefined,
    cost: apiVisit.cost || undefined
  };
}

/**
 * Convertește VisitFormState (camelCase) la API request format (snake_case)
 * Adaptat la tipurile reale din types/visit.ts
 */
export function toApiVisitForm(form: VisitFormState): ApiVisitFormData {
  return {
    vitals: form.vitals ? {
      temperature: form.vitals.temperature,
      heart_rate: form.vitals.heartRate,
      respiratory_rate: form.vitals.respiratoryRate,
      weight: form.vitals.weight,
      bcs: form.vitals.bcs,
      crt: form.vitals.crt,
      pain_score: form.vitals.painScore,
      mucosa: form.vitals.mucosa,
      recorded_at: form.vitals.recordedAt
    } : undefined,
    
    anamnesis: form.anamnesis ? {
      nutrition: form.anamnesis.nutrition,
      deworming: form.anamnesis.deworming,
      deworming_last: form.anamnesis.dewormingLast,
      current_meds: form.anamnesis.currentMeds,
      allergies: form.anamnesis.allergies,
      environment: form.anamnesis.environment,
      reproduction: form.anamnesis.reproduction
    } : undefined,
    
    exam: form.exam ? {
      systems: form.exam.systems,
      system_notes: form.exam.systemNotes,
      notes: form.exam.notes,
      all_normal: form.exam.allNormal
    } : undefined,
    
    problems: form.problems?.map(p => ({
      id: p.id,
      description: p.description,
      severity: p.severity,
      type: p.type,
      icd_code: p.icdCode,
      resolved: p.resolved
    })),
    
    plan: form.plan ? {
      investigations: form.plan.investigations,
      treatments: form.plan.treatments,
      home_care: form.plan.homeCare,
      recall_date: form.plan.recallDate,
      recall_reason: form.plan.recallReason
    } : undefined,
    
    treatments: form.treatments?.map(t => ({
      id: t.id,
      drug: t.drug,
      dose: t.dose,
      route: t.route,
      time: t.time,
      qty: t.qty,
      uom: t.uom,
      lot: t.lot,
      exp: t.exp
    })),
    
    vaccination: form.vaccination ? {
      product: form.vaccination.product,
      lot: form.vaccination.lot,
      exp: form.vaccination.exp,
      scheme: form.vaccination.scheme,
      next_due: form.vaccination.nextDue
    } : undefined,
    
    notes: form.notes,
    recommendations: form.recommendations
  };
}

/**
 * Helper: normalizează date pentru API (ISO string)
 */
export function normalizeDateForApi(date: Date | string | undefined): string | undefined {
  if (!date) return undefined;
  if (typeof date === 'string') return date;
  return date.toISOString();
}
